import ControlPanelContainer from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelContainer';
import ControlPanelDivider from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelDivider';
import AttributeEditorTable from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/AttributeEditorTable';
import { ATTRIBUTES } from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceInquiryPanel';
import CaseDataManagerSchemaSelect from 'dashboard/components/CaseDataManagerSchemaSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Template } from 'dashboard/models/template';
import { ResourceInquiryAttribute } from 'dashboard/modules/ResourceInquiry';
import { ModuleResourceInquiryV2 } from 'dashboard/modules/ResourceInquiryV2';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label } from 'design-system/components/Text';
import { JsonSchemaObject } from 'lib/json-schema';
import { nameDisplay } from 'lib/utils/template';

export interface ResourceInquiryV2PanelProps {
  module: ModuleResourceInquiryV2;
  dataSchema: JsonSchemaObject;
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  updateModule: (module: ModuleResourceInquiryV2) => void;
}

const ResourceInquiryV2Panel = ({
  module,
  dataSchema,
  enabledInquiryTemplates,
  updateModule,
}: ResourceInquiryV2PanelProps) => {
  const handleSetInquiryResourceVariable = (variableToken: string) => {
    updateModule({
      ...module,
      'variable-token': variableToken,
    });
  };

  const handleSetInquiryTemplate = (
    inquiryTemplate: ModuleResourceInquiryV2['inquiry-template'],
  ) => {
    updateModule({
      ...module,
      'inquiry-template': inquiryTemplate,
    });
  };

  const handleSetHideEmptyAttributes = (hideEmptyAttributes: boolean) => {
    updateModule({
      ...module,
      'hide-empty-attributes': hideEmptyAttributes,
    });
  };

  const updateAttributes = (attributes: ResourceInquiryAttribute[]) => {
    updateModule({ ...module, attributes });
  };

  const inquiryTemplateOptions = [
    {
      label: 'Any',
      value: '',
    },
    ...enabledInquiryTemplates
      .map((inquiryTemplate) => {
        return {
          label: nameDisplay(inquiryTemplate),
          value: inquiryTemplate.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  return (
    <>
      <ControlPanelContainer>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={2} />

        <Label id='inquiry-resource-variable-selector-label' size='md' weight='bold'>
          Resource
        </Label>
        <CaseDataManagerSchemaSelect
          jsonSchema={dataSchema}
          inputValue={module['variable-token']}
          onValueChange={handleSetInquiryResourceVariable}
        />

        {enabledInquiryTemplates.length > 1 && (
          <>
            <Spacer height={2} />
            <Label id='inquiry-template-selector-label' size='md' weight='bold'>
              Inquiry template
            </Label>
            <ReactSelect
              id='resource-inquiry-panel__inquiry-template-selector'
              aria-labelledby='inquiry-template-selector-label'
              options={inquiryTemplateOptions}
              value={module['inquiry-template'] ?? ''}
              onChange={handleSetInquiryTemplate}
              portal
            />
          </>
        )}
        <ControlPanelDivider full />
      </ControlPanelContainer>
      <AttributeEditorTable<ResourceInquiryAttribute>
        allAttributes={ATTRIBUTES}
        moduleAttributes={module.attributes}
        hideEmptyAttributes={module['hide-empty-attributes']}
        updateAttributes={updateAttributes}
        updateHideEmptyAttributes={handleSetHideEmptyAttributes}
      />
    </>
  );
};

export default ResourceInquiryV2Panel;
